"use client";

import { useLocale } from "@/lib/i18n";
import { localizedNoteFields } from "@/lib/notes/localizations";

type Variant = "index-row" | "post-header";

type Props = {
  slug: string;
  title: string;
  lede: string;
  variant: Variant;
};

/**
 * Renders the title + lede for a note, swapping to the active locale's
 * translation if one exists. EN renders the canonical fields. Falls back
 * to EN if a translation is missing. Marker chip ("EN ORIGINAL") shows on
 * non-EN locales since post bodies remain English-canonical for now.
 */
export function LocalizedNoteHeader({ slug, title, lede, variant }: Props) {
  const { locale } = useLocale();
  const localized = localizedNoteFields(slug, locale);

  const t = localized?.title ?? title;
  const l = localized?.lede ?? lede;

  if (variant === "index-row") {
    return (
      <div>
        <h2
          className="text-2xl lg:text-4xl leading-tight text-[var(--color-text)] group-hover/note:text-[var(--color-blue)] transition-colors"
          style={{
            fontFamily: "var(--font-display)",
            fontVariationSettings: '"wght" 600',
          }}
        >
          {t}
        </h2>
        <p className="mt-3 text-[var(--color-text-dim)] max-w-[60ch]">{l}</p>
      </div>
    );
  }

  // post-header variant
  return (
    <>
      <h1
        className="text-[clamp(40px,6vw,80px)] leading-[1] tracking-[-0.02em] mb-5"
        style={{
          fontFamily: "var(--font-display)",
          fontVariationSettings: '"wght" 800, "wdth" 75',
        }}
      >
        {t}
      </h1>
      <p className="text-lg lg:text-xl text-[var(--color-text-dim)] leading-relaxed">
        {l}
      </p>
      {locale !== "en" ? (
        <div className="mt-5 inline-flex items-center gap-2 microcaps text-[10px] text-[var(--color-text-mute)] px-2.5 py-1 rounded-sm border border-[var(--color-hairline)]">
          <span aria-hidden className="block h-1.5 w-1.5 rounded-full bg-amber-400" />
          {locale === "sq" ? "Trupi i shkrimit në EN" : "النص الأصلي بالإنجليزية"}
        </div>
      ) : null}
    </>
  );
}
